import React, { useState, useEffect, useRef } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import { calculateMultipleBoundingBoxes } from './BoundingBoxCalculator';
import { calculateMultiplePolygons } from './PolygonCalculator';

/**
 * Debug-Komponente zur Visualisierung von Bounding Boxes
 * Zeichnet die Bounding Boxes als Overlay auf dem Canvas
 */
export const BoundingBoxDebug = ({ 
    objectSpawners = [], 
    imageWidth = 640, 
    imageHeight = 640,
    showDebug = true,
    annotationType = 'boundingBox' // 'boundingBox' oder 'segmentation'
}) => {
    const { gl } = useThree();
    const [boundingBoxes, setBoundingBoxes] = useState([]);
    const [polygons, setPolygons] = useState([]);
    const overlayRef = useRef(null);
    const frameCountRef = useRef(0);

    useFrame((state) => {
        if (!showDebug || objectSpawners.length === 0) {
            setBoundingBoxes([]);
            setPolygons([]);
            frameCountRef.current = 0;
            return;
        }

        // PERFORMANCE: Throttle Polygon-Berechnung drastisch für Echtzeit - nur alle 60 Frames (1x pro Sekunde bei 60fps)
        frameCountRef.current++;
        const shouldCalculate = annotationType === 'boundingBox' || frameCountRef.current % 60 === 0;
        
        if (!shouldCalculate && annotationType === 'segmentation') {
            return; // Überspringe Berechnung für diesen Frame
        }

        // Verwende die aktuelle Kamera aus dem Frame-State
        // Diese ist immer auf dem neuesten Stand (inkl. OrbitControls-Änderungen)
        const currentCamera = state.camera;
        
        // Hole die tatsächliche Canvas-Render-Größe
        const canvas = gl.domElement;
        const actualWidth = canvas.width || imageWidth;
        const actualHeight = canvas.height || imageHeight;

        if (annotationType === 'segmentation') {
            // Berechne Polygone für Segmentation
            const allPolygons = [];
            
            objectSpawners.forEach(spawner => {
                if (spawner.ref && spawner.ref.current) {
                    try {
                        const objects = spawner.ref.current.getObjects();
                        if (objects && objects.length > 0) {
                            // Berechne Polygone mit der aktuellen Kamera
                            // WICHTIG: Verwendet die gleichen Standardwerte wie beim Export
                            // für identische Ansicht im Editor und beim Export
                            const polys = calculateMultiplePolygons(
                                objects,
                                currentCamera,
                                actualWidth,
                                actualHeight
                            );
                            
                            // Füge Klassen-Informationen hinzu
                            const polysWithClass = polys.map(poly => ({
                                ...poly,
                                classId: spawner.classId !== undefined ? spawner.classId : 0,
                                className: spawner.className || `Class ${spawner.classId || 0}`
                            }));
                            
                            allPolygons.push(...polysWithClass);
                        }
                    } catch (error) {
                        console.warn('Error calculating polygons for debug:', error);
                    }
                }
            });
            
            setPolygons(allPolygons);
            setBoundingBoxes([]);
        } else {
            // Berechne Bounding Boxes für Object Detection
            const allBoundingBoxes = [];
            
            objectSpawners.forEach(spawner => {
                if (spawner.ref && spawner.ref.current) {
                    try {
                        const objects = spawner.ref.current.getObjects();
                        if (objects && objects.length > 0) {
                            // Berechne Bounding Boxes mit der aktuellen Kamera
                            const bboxes = calculateMultipleBoundingBoxes(
                                objects,
                                currentCamera,
                                actualWidth,
                                actualHeight
                            );
                            
                            // Füge Klassen-Informationen hinzu
                            const bboxesWithClass = bboxes.map(bbox => ({
                                ...bbox,
                                classId: spawner.classId !== undefined ? spawner.classId : 0,
                                className: spawner.className || `Class ${spawner.classId}`
                            }));
                            
                            allBoundingBoxes.push(...bboxesWithClass);
                        }
                    } catch (error) {
                        console.warn('Error calculating bounding boxes:', error);
                    }
                }
            });
            
            setBoundingBoxes(allBoundingBoxes);
            setPolygons([]);
        }
    });

    useEffect(() => {
        if (!showDebug) {
            // Entferne Overlay wenn Debug ausgeschaltet
            const canvas = gl.domElement;
            const container = canvas.parentElement;
            if (container) {
                const overlay = container.querySelector('.bounding-box-overlay');
                if (overlay) overlay.remove();
            }
            return;
        }

        const canvas = gl.domElement;
        const container = canvas.parentElement;
        
        if (!container) return;

        // Stelle sicher, dass Container position: relative hat
        const containerStyle = window.getComputedStyle(container);
        if (containerStyle.position === 'static') {
            container.style.position = 'relative';
        }

        // Erstelle oder hole Overlay-Element
        let overlay = container.querySelector('.bounding-box-overlay');
        if (!overlay) {
            overlay = document.createElement('div');
            overlay.className = 'bounding-box-overlay';
            overlay.style.cssText = `
                position: absolute;
                pointer-events: none;
                z-index: 1000;
            `;
            container.appendChild(overlay);
        }
        
        overlayRef.current = overlay;

        return () => {
            // Cleanup: Entferne Overlay beim Unmount
            if (overlayRef.current && overlayRef.current.parentElement) {
                overlayRef.current.remove();
            }
        };
    }, [showDebug, gl]);

    // Zeichne Bounding Boxes oder Polygone auf Overlay
    useEffect(() => {
        if (!showDebug || !overlayRef.current || (boundingBoxes.length === 0 && polygons.length === 0)) {
            if (overlayRef.current) {
                overlayRef.current.innerHTML = '';
            }
            return;
        }

        const canvas = gl.domElement;
        const overlay = overlayRef.current;
        const container = canvas.parentElement;
        
        if (!container) return;
        
        // Hole die tatsächliche Render-Größe (canvas.width/height)
        // Diese sollte mit der Berechnung übereinstimmen
        const renderWidth = canvas.width || imageWidth;
        const renderHeight = canvas.height || imageHeight;
        
        // Hole die Display-Größe (wie das Canvas auf dem Bildschirm angezeigt wird)
        const displayWidth = canvas.offsetWidth;
        const displayHeight = canvas.offsetHeight;
        
        // Berechne Skalierungsfaktor zwischen Display-Größe und Render-Größe
        const scaleX = displayWidth / renderWidth;
        const scaleY = displayHeight / renderHeight;
        
        // Setze Overlay-Größe und Position auf Container-Größe
        // Das Canvas ist zentriert, daher muss das Overlay auch zentriert sein
        const containerRect = container.getBoundingClientRect();
        overlay.style.width = `${displayWidth}px`;
        overlay.style.height = `${displayHeight}px`;
        
        // Positioniere das Overlay genau über dem Canvas
        // Das Canvas hat position: absolute, top: 50%, left: 50%, transform: translate(-50%, -50%)
        const canvasRect = canvas.getBoundingClientRect();
        const containerRect2 = container.getBoundingClientRect();
        overlay.style.left = `${canvasRect.left - containerRect2.left}px`;
        overlay.style.top = `${canvasRect.top - containerRect2.top}px`;

        // Erstelle SVG für Vektorgrafiken
        const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        svg.setAttribute('width', `${displayWidth}px`);
        svg.setAttribute('height', `${displayHeight}px`);
        svg.style.position = 'absolute';
        svg.style.top = '0';
        svg.style.left = '0';
        svg.style.width = '100%';
        svg.style.height = '100%';

        if (annotationType === 'segmentation' && polygons.length > 0) {
            // Zeichne Polygone für Segmentation
            polygons.forEach((poly, index) => {
                if (!poly.points || poly.points.length < 3) return;
                
                const polygon = document.createElementNS('http://www.w3.org/2000/svg', 'polygon');
                const pointsString = poly.points.map(p => 
                    `${p.x * scaleX},${p.y * scaleY}`
                ).join(' ');
                
                polygon.setAttribute('points', pointsString);
                polygon.setAttribute('fill', `hsla(${(poly.classId || 0) * 60}, 100%, 50%, 0.1)`);
                polygon.setAttribute('stroke', `hsl(${(poly.classId || 0) * 60}, 100%, 50%)`);
                polygon.setAttribute('stroke-width', '2');
                polygon.setAttribute('stroke-dasharray', '5,5');
                
                svg.appendChild(polygon);

                // Füge Label hinzu (am ersten Punkt)
                if (poly.points.length > 0) {
                    const firstPoint = poly.points[0];
                    const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
                    text.setAttribute('x', firstPoint.x * scaleX);
                    text.setAttribute('y', Math.max(firstPoint.y * scaleY - 5, 10));
                    text.setAttribute('fill', `hsl(${(poly.classId || 0) * 60}, 100%, 50%)`);
                    text.setAttribute('font-size', '12px');
                    text.setAttribute('font-family', 'Arial, sans-serif');
                    text.setAttribute('font-weight', 'bold');
                    text.textContent = `${poly.className || `Class ${poly.classId}`} (${poly.points.length} pts)`;
                    svg.appendChild(text);
                }
            });
        } else if (boundingBoxes.length > 0) {
            // Zeichne Bounding Boxes für Object Detection
            boundingBoxes.forEach((bbox, index) => {
                const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
                // Transformiere von Render-Koordinaten zu Display-Koordinaten
                const x = bbox.x * scaleX;
                const y = bbox.y * scaleY;
                const width = bbox.width * scaleX;
                const height = bbox.height * scaleY;

                rect.setAttribute('x', x);
                rect.setAttribute('y', y);
                rect.setAttribute('width', width);
                rect.setAttribute('height', height);
                rect.setAttribute('fill', 'none');
                rect.setAttribute('stroke', `hsl(${(bbox.classId || 0) * 60}, 100%, 50%)`);
                rect.setAttribute('stroke-width', '2');
                rect.setAttribute('stroke-dasharray', '5,5');
                
                svg.appendChild(rect);

                // Füge Label hinzu
                const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
                text.setAttribute('x', x);
                text.setAttribute('y', Math.max(y - 5, 10));
                text.setAttribute('fill', `hsl(${(bbox.classId || 0) * 60}, 100%, 50%)`);
                text.setAttribute('font-size', '12px');
                text.setAttribute('font-family', 'Arial, sans-serif');
                text.setAttribute('font-weight', 'bold');
                text.textContent = `${bbox.className || `Class ${bbox.classId}`} (${Math.round(width)}x${Math.round(height)})`;
                svg.appendChild(text);
            });
        }

        overlay.innerHTML = '';
        overlay.appendChild(svg);
    }, [boundingBoxes, polygons, annotationType, showDebug, gl, imageWidth, imageHeight]);

    return null;
};

